// ─────────────────────────────────────────────────────────────────────────────
// JEDI "view as role", artist-surface role simulator.
//
// Port of role-perms.js's jedi view-as switcher (sessionStorage key
// `ac_jedi_view_as`). The artist jedi (whitsettband@gmail.com) can preview the
// app AS any artist-side seat, Artist owner, Band member, Tour manager,
// Production, Merch, Content, to verify the role gates (which tabs show, what
// money is visible) without a second account.
//
// COSMETIC ONLY. This swaps the *client-side* role on the cached user so the UI
// gates (canSeeBooking / showLifetime / nav tabs) render as the simulated seat.
// The worker still authorizes every data call with the real (jedi) token, so it
// proves what a seat *sees in the UI*, not server-side enforcement, exactly the
// QA tool the role-gate pass needs. Non-jedi users can never trigger it (the
// email gate + applyViewAs both fail closed).
// ─────────────────────────────────────────────────────────────────────────────

import type { User } from './types';

const SS_KEY = 'ac_jedi_view_as';

/* acp-preview 2026-07-24 (Ryan) — second view-as dimension: PLAN. The jedi
   can view the app as ACP (plan_tier 'acp') independently of the role
   simulator, to build/QA the Pro variants of Booking / Venue assets /
   Contacts and the coming ACP crew role types without flipping a real row.
   COSMETIC ONLY, same contract as the role switcher: the worker never sees
   this — when ACP grows server-side gates (e.g. ACP-only crew invites), the
   worker must grant the jedi account pro server-side; this toggle only picks
   which VARIANT renders. */
const SS_PLAN_KEY = 'ac_jedi_view_plan';

// Hard email gate, mirrors role-perms.js ARTIST_JEDI_EMAIL. Flipping users.role
// to 'jedi' in D1 grants nothing here, the email is the authority.
export const ARTIST_JEDI_EMAIL = 'whitsettband@gmail.com';

/** True only for the artist-surface jedi (by email, not by role string). */
export function isArtistJedi(user: User | null | undefined): boolean {
  if (!user) return false;
  return String(user.email || '').toLowerCase() === ARTIST_JEDI_EMAIL;
}

/** The roles the jedi switcher can simulate, in dropdown order. Each value is
 *  the canonical role string a "view" collapses to (Production → foh_engineer,
 *  Content → content_creator), identical to the legacy dropdown. */
export interface ViewAsOption {
  readonly key: string;
  readonly label: string;
}
export const VIEW_AS_OPTIONS: ReadonlyArray<ViewAsOption> = [
  { key: 'jedi', label: 'Jedi (god mode)' },
  { key: 'artist', label: 'Artist (owner)' },
  /* av-view-as 2026-07-28 (Ryan: "artist should be band member AV, it needs
     the inbox") — the AV seat was missing from the simulator, which mattered
     the moment Pro mobile QA started: the OWNER view ('artist') deliberately
     has no Inbox tab (primary-no-inbox 2026-07-27, MobileTabBar), so previewing
     the artist-side surface WITH messaging requires the AV seat. AV keeps full
     artist-side tabs (roleTabs FULL / mobile normalizeRoleForTabs → 'artist')
     but is NOT the primary, so is_band_primary stays false below — which is
     exactly the real AV member's shape. */
  { key: 'band_member_av', label: 'Band member · AV' },
  { key: 'band_member', label: 'Band member' },
  { key: 'tour_manager', label: 'Tour manager' },
  { key: 'foh_engineer', label: 'Production' },
  { key: 'merch_manager', label: 'Merch manager' },
  { key: 'content_creator', label: 'Content' },
  /* roster-view-as 2026-07-28 (Ryan: "everything is changing on role switch
     EXCEPT the home screen. I know we build different home screens for each
     view") — the per-role HOME trees that actually exist are the roster ones:
     MobileDashboard branches to <RosterHome> only when the effective role
     normalizes to label / manager / booking_agent (rhRole, ~line 2798), and
     RosterHome then swaps LabelMain / MgmtMain / BookingMain. None of those
     roles were offered here, so the distinct homes were unreachable from the
     simulator while every artist-side seat shares ONE home tree.

     Artist-side seats vary that tree in exactly two places today — the
     Trending card (jedi/artist/AV only) and the Merch module (money group) —
     which is why swapping between them looks like nothing happened. That's a
     build gap on the artist home, not a switcher fault. */
  { key: 'label', label: 'Label · roster home' },
  { key: 'manager', label: 'Management · roster home' },
  { key: 'booking_agent', label: 'Booking · roster home' },
];

/** Current view-as plan override: 'acp' or null (real plan). */
export function getViewPlan(): 'acp' | null {
  try {
    return sessionStorage.getItem(SS_PLAN_KEY) === 'acp' ? 'acp' : null;
  } catch {
    return null;
  }
}

/** Set/clear the simulated plan. Same flush+reload contract as setViewAs. */
export function setViewPlan(plan: 'acp' | null): void {
  try {
    if (plan === 'acp') sessionStorage.setItem(SS_PLAN_KEY, 'acp');
    else sessionStorage.removeItem(SS_PLAN_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.indexOf('mapi:') === 0) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/** Current view-as role, or null when off (real role). */
export function getViewAs(): string | null {
  try {
    const v = sessionStorage.getItem(SS_KEY);
    return v && v !== 'jedi' ? v : null;
  } catch {
    return null;
  }
}

/** Set (or clear, with 'jedi'/null) the simulated role. Flushes role-scoped
 *  session caches and hard-reloads so every screen refetches its user through
 *  applyViewAs and re-gates. A reload is the bulletproof path for a jedi-only QA
 *  tool, it avoids threading a role context through every ported screen. */
export function setViewAs(role: string | null): void {
  try {
    if (!role || role === 'jedi') sessionStorage.removeItem(SS_KEY);
    else sessionStorage.setItem(SS_KEY, role);
  } catch {
    /* sessionStorage unavailable, nothing to persist */
  }
  // Flush the mobile/legacy role-scoped session caches (same shape role-perms.js
  // clears on a view-as flip) so no previous-role blob paints post-reload.
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.indexOf('mapi:') === 0) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/** Apply the jedi view-as override to a freshly-loaded user. Returns the user
 *  unchanged when: no override set, the override is 'jedi', or the user is not
 *  the artist jedi (fail closed). Otherwise returns a clone with the role gates'
 *  inputs swapped to the simulated seat:
 *    · role + membership_role → the simulated role
 *    · is_band_primary → true ONLY for the 'artist' (owner) view; false for
 *      every other seat, so canSeeBooking can't short-circuit to money.
 *      av-view-as 2026-07-28: 'band_member_av' correctly lands false here and
 *      still resolves to full access + money through membership_role
 *      (roleTabs.effectiveRoleKey → FULL, booking.BOOKING_SEATS → true), which
 *      is exactly how a REAL AV member's row behaves. */
export function applyViewAs(user: User | undefined): User | undefined {
  if (!user) return user;
  if (!isArtistJedi(user)) return user;
  const role = getViewAs();
  const plan = getViewPlan();
  if (!role && !plan) return user;
  let out: User = { ...user };
  if (role) {
    out = { ...out, role, membership_role: role, is_band_primary: role === 'artist' };
  }
  /* acp-preview: stamping plan_tier here means isPro() (lib/plan.ts) flips
     everywhere with no per-screen wiring — dashboard.ts applies this to every
     user read. Composes with the role simulator (e.g. "Tour manager on ACP"). */
  if (plan) {
    out = { ...out, plan_tier: plan };
  }
  return out;
}
